import Button from "@syntensor/common/components/button";
import getCopy from "@syntensor/common/data/copy";
import Loader from "@syntensor/common/components/loader";
import { IGroup } from "@syntensor/common/types";
import {
  BookmarksIcon,
  EIconSize,
  TrashIcon,
} from "@syntensor/common/components/icons";

import {
  IsUserInAdminGroup,
  IsUserInGroupAdminGroup,
} from "../auth/auth_context";

import styles from "./group_box.module.css";
import { useEffect, useMemo, useState } from "react";
import { deleteGroup } from "@syntensor/common/data/fetch_data";
import { navigateWithQueryParams } from "@syntensor/common/browser_history";

export default function GroupBox({
  id,
  name,
  projectsCount = 0,
  onDelete = null,
  onClone = null,
}: IGroup) {
  const isUserInAdminGroup = IsUserInAdminGroup();
  const [isUserInGroupAdminGroup, setIsUserInGroupAdminGroup] =
    useState(isUserInAdminGroup);

  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);

  useEffect(() => {
    async function handler() {
      const admin = await IsUserInGroupAdminGroup(parseInt(id));
      setIsUserInGroupAdminGroup(admin);
    }

    if (!isUserInAdminGroup && id) {
      handler();
    }
  }, [id]);

  const projectsCountTxt = useMemo(() => {
    return getCopy("groups_group_footer", { studiesCount: projectsCount });
  }, []);

  const onDeleteClick = async () => {
    if (!isDeleteInProgress) {
      const confirmed = window.confirm(
        getCopy("groups_delete_confirm", { name })
      );
      if (confirmed) {
        setIsDeleteInProgress(true);
        await deleteGroup(parseInt(id));

        if (onDelete) {
          await onDelete();
          setIsDeleteInProgress(false);
        }
      }
    }
  };

  const onCloneClick = () => {
    if (onClone) {
      navigateWithQueryParams(`/groups/create/${id}`);
    }
  };

  return (
    <div className={styles.studyBox} data-testid="projects_project_study-box">
      {isUserInAdminGroup && (
        <div className={styles.topBtns}>
          {onClone && (
            <button
              className={styles.topBtn}
              onClick={onCloneClick}
              title={getCopy("groups_group_clone")}
            >
              <BookmarksIcon size={EIconSize.SMALL} />
            </button>
          )}
          {onDelete && (
            <button
              className={styles.topBtn}
              onClick={onDeleteClick}
              disabled={isDeleteInProgress}
              title={getCopy("projects_project_delete")}
            >
              {isDeleteInProgress && <Loader size="18px" />}
              {!isDeleteInProgress && <TrashIcon size={EIconSize.SMALL} />}
            </button>
          )}
        </div>
      )}
      <div className={styles.header}>
        <h3 className={styles.title}>{name}</h3>
        <ul className={styles.tags}></ul>
      </div>
      <div className={styles.meta}></div>
      <div className={styles.description}></div>
      <div className={styles.footer}>
        <div>
          <p>{projectsCountTxt}</p>
        </div>
        <div className={styles.footerBtns}>
          {(isUserInAdminGroup || isUserInGroupAdminGroup) && (
            <Button
              to={`/groups/${id}/edit`}
              role="subtle"
              data-testid="projects_project_study-configure"
            >
              {getCopy("projects_project_configure")}
            </Button>
          )}
          <Button
            to={`/groups/${id}`}
            onClick={() => {
              sessionStorage.setItem("groupName", name);
            }}
            role="primary"
            data-testid="projects_project_study-open"
          >
            {getCopy("projects_project_open")}
          </Button>
        </div>
      </div>
    </div>
  );
}
